/**
 * @module config
 * @description Manages the shared adapter configuration singleton and runtime
 * validation helpers. The module ensures adapter domains are configured once
 * and exposed through a typed contract before adapters are resolved.
 */

import { getProvidersForDomain } from "./constants";
import { AdapterConfigurationError } from "./errors";
import type {
  AdapterConfig,
  AdapterConfigForDomain,
  AdapterDomain,
  DomainAdapterConfig,
  ResolvableAdapterDomain,
} from "./types";

/** Set of valid adapter domain names */
const VALID_DOMAINS: readonly ResolvableAdapterDomain[] = [
  "db",
  "auth",
  "payments",
  "ai",
  "storage",
  "admin",
  "logger",
  "cache",
  "queue",
  "events",
  "deploy",
];
const VALID_DOMAIN_SET: ReadonlySet<string> = new Set(VALID_DOMAINS);
const REGISTRY_VALIDATED_DOMAINS: readonly AdapterDomain[] = [
  "db",
  "auth",
  "payments",
  "ai",
  "storage",
  "admin",
  "logger",
];
const REGISTRY_VALIDATED_DOMAIN_SET: ReadonlySet<string> = new Set(
  REGISTRY_VALIDATED_DOMAINS,
);

/** Global configuration state - null until configureAdapters() is called */
let _config: AdapterConfig | null = null;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  if (!isRecord(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function cloneConfigValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(cloneConfigValue);
  }
  if (isPlainRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, cloneConfigValue(item)]),
    );
  }
  return value;
}

function deepFreeze<T>(value: T, seen = new WeakSet<object>()): T {
  if (
    (typeof value !== "object" && typeof value !== "function") ||
    value === null
  ) {
    return value;
  }

  const objectValue = value as object;
  if (seen.has(objectValue)) {
    return value;
  }
  seen.add(objectValue);

  for (const item of Object.values(objectValue)) {
    deepFreeze(item, seen);
  }
  return Object.freeze(value);
}

function cloneDomainConfig<T extends DomainAdapterConfig>(config: T): T {
  const clone: DomainAdapterConfig = {
    provider: config.provider,
  };
  if (config.apiKey !== undefined) {
    clone.apiKey = config.apiKey;
  }
  if (config.baseUrl !== undefined) {
    clone.baseUrl = config.baseUrl;
  }
  if (config.options !== undefined) {
    clone.options = cloneConfigValue(config.options) as Record<string, unknown>;
  }
  return deepFreeze(clone) as T;
}

function cloneAdapterConfig(config: AdapterConfig): AdapterConfig {
  const clone: AdapterConfig = {};
  if (config.db !== undefined) {
    clone.db = cloneDomainConfig(config.db);
  }
  if (config.auth !== undefined) {
    clone.auth = cloneDomainConfig(config.auth);
  }
  if (config.payments !== undefined) {
    clone.payments = cloneDomainConfig(config.payments);
  }
  if (config.ai !== undefined) {
    clone.ai = cloneDomainConfig(config.ai);
  }
  if (config.storage !== undefined) {
    clone.storage = cloneDomainConfig(config.storage);
  }
  if (config.admin !== undefined) {
    clone.admin = cloneDomainConfig(config.admin);
  }
  if (config.logger !== undefined) {
    clone.logger = cloneDomainConfig(config.logger);
  }
  if (config.cache !== undefined) {
    clone.cache = cloneDomainConfig(config.cache);
  }
  if (config.queue !== undefined) {
    clone.queue = cloneDomainConfig(config.queue);
  }
  if (config.events !== undefined) {
    clone.events = cloneDomainConfig(config.events);
  }
  if (config.deploy !== undefined) {
    clone.deploy = cloneDomainConfig(config.deploy);
  }
  return deepFreeze(clone);
}

function isAdapterDomain(value: string): value is ResolvableAdapterDomain {
  return VALID_DOMAIN_SET.has(value);
}

function isRegistryValidatedDomain(
  domain: ResolvableAdapterDomain,
): domain is AdapterDomain {
  return REGISTRY_VALIDATED_DOMAIN_SET.has(domain);
}

/**
 * Validates that a domain configuration object is well-formed.
 * Uses TypeScript assertion to refine the type after validation.
 *
 * @param domain - The domain being configured (for error messages)
 * @param cfg - The configuration object to validate
 * @throws {Error} If the configuration is invalid
 *
 * @internal
 */
function validateDomainConfig(
  domain: ResolvableAdapterDomain,
  cfg: unknown,
): asserts cfg is DomainAdapterConfig {
  // Check that cfg is a plain object (reject Date, Map, RegExp, class instances, etc.)
  if (!isPlainRecord(cfg)) {
    throw new AdapterConfigurationError(
      `adapters.${domain}: expected a plain object, got ${Array.isArray(cfg) ? "array" : typeof cfg}`,
      "INVALID_CONFIG",
    );
  }
  const obj = cfg;

  // Provider is required and must be a non-empty string
  if (typeof obj.provider !== "string" || obj.provider.length === 0) {
    throw new AdapterConfigurationError(
      `adapters.${domain}.provider: expected a non-empty string`,
      "INVALID_CONFIG",
    );
  }

  if (isRegistryValidatedDomain(domain)) {
    const providerIds = getProvidersForDomain(domain).map(
      (provider) => provider.id,
    );
    if (!providerIds.includes(obj.provider)) {
      throw new AdapterConfigurationError(
        `adapters.${domain}.provider: unsupported provider "${obj.provider}". ` +
          `Allowed providers: ${providerIds.join(", ")}`,
        "INVALID_CONFIG",
      );
    }
  }

  // Optional apiKey must be a string if provided
  if (obj.apiKey !== undefined && typeof obj.apiKey !== "string") {
    throw new AdapterConfigurationError(
      `adapters.${domain}.apiKey: expected a string`,
      "INVALID_CONFIG",
    );
  }

  // Optional baseUrl must be a string if provided
  if (obj.baseUrl !== undefined && typeof obj.baseUrl !== "string") {
    throw new AdapterConfigurationError(
      `adapters.${domain}.baseUrl: expected a string`,
      "INVALID_CONFIG",
    );
  }

  // Optional options must be a plain object if provided
  if (obj.options !== undefined && !isPlainRecord(obj.options)) {
    throw new AdapterConfigurationError(
      `adapters.${domain}.options: expected a plain object`,
      "INVALID_CONFIG",
    );
  }
}

/**
 * Configure the adapter system with provider implementations.
 * Must be called exactly once at application startup, before adapters are used.
 *
 * @param config - Configuration object specifying provider for each adapter domain
 * @throws {Error} If config is not a valid object or contains invalid domain configurations
 *
 * @example
 * ```ts
 * import { configureAdapters } from '@geenius/adapters';
 *
 * // Configure all domains at startup
 * configureAdapters({
 *   db: { provider: 'convex' },
 *   auth: { provider: 'better-auth' },
 *   ai: { provider: 'openai', apiKey: process.env.OPENAI_API_KEY },
 *   storage: { provider: 'r2', apiKey: process.env.R2_API_KEY },
 *   payments: { provider: 'stripe' },
 * });
 * ```
 */
export function configureAdapters(config: AdapterConfig): void {
  // Validate that config is a plain object (reject Date, Map, RegExp, etc.)
  if (!isPlainRecord(config)) {
    throw new AdapterConfigurationError(
      "configureAdapters: expected a plain object",
      "INVALID_CONFIG",
    );
  }

  // Validate each domain configuration that was provided
  for (const [key, value] of Object.entries(config)) {
    // Check that the domain name is valid
    if (!isAdapterDomain(key)) {
      throw new AdapterConfigurationError(
        `configureAdapters: unknown domain "${key}". Valid domains: ${VALID_DOMAINS.join(", ")}`,
        "INVALID_CONFIG",
      );
    }

    // Validate the domain configuration if one was provided
    if (value !== undefined) {
      validateDomainConfig(key, value);
    }
  }

  // Store the validated configuration globally
  _config = cloneAdapterConfig(config);
}

/**
 * Retrieve the global adapter configuration.
 * Safe to call multiple times from all modules.
 *
 * @returns The currently configured adapter configuration
 * @throws {Error} If configureAdapters() has not been called yet
 *
 * @example
 * ```ts
 * const config = getAdapterConfig();
 * logger.info(config.db?.provider); // 'convex'
 * ```
 */
export function getAdapterConfig(): AdapterConfig {
  if (!_config) {
    throw new AdapterConfigurationError(
      "Adapters not configured. Call configureAdapters() first.",
      "MISSING_CONFIG",
    );
  }
  return cloneAdapterConfig(_config);
}

/**
 * Check if the adapter system has been configured.
 * Useful for lazy initialization or error handling.
 *
 * @returns True if configureAdapters() has been called, false otherwise
 *
 * @example
 * ```ts
 * if (!isAdaptersConfigured()) {
 *   throw new AdapterConfigurationError('App not initialized - configure adapters first', 'MISSING_CONFIG');
 * }
 * ```
 */
export function isAdaptersConfigured(): boolean {
  return _config !== null;
}

/**
 * Reset the adapter configuration to an uninitialized state.
 * Primarily useful for testing, to avoid state leakage between test cases.
 *
 * @example
 * ```ts
 * beforeEach(() => {
 *   resetAdapterConfig(); // Clear state from previous test
 *   configureAdapters(testConfig);
 * });
 * ```
 */
export function resetAdapterConfig(): void {
  _config = null;
}

/**
 * Get the configuration for a specific adapter domain.
 * Returns null if the domain is not configured.
 *
 * @param domain - The adapter domain to get configuration for
 * @returns Configuration for the domain, or null if not configured
 *
 * @example
 * ```ts
 * const dbConfig = getDomainConfig('db');
 * if (dbConfig) {
 *   logger.info(`Using ${dbConfig.provider} for database`);
 * }
 * ```
 */
export function getDomainConfig<D extends ResolvableAdapterDomain>(
  domain: D,
): AdapterConfigForDomain<D> | null {
  const config = getAdapterConfig();
  return (config[domain] as AdapterConfigForDomain<D> | undefined) ?? null;
}
